import Chip from '@mui/material/Chip';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import type { Feature } from '@/content/features';
import { IllustrationFrame } from '@/components/illustrations/IllustrationFrame';
import { TONE_DOT_CLASS } from '@/lib/tone';

function TicketsMock(): JSX.Element {
  const rows = [
    { label: 'Printer offline — Site B, Rm 214', status: 'AI draft ready', tone: 'watch' as const },
    { label: 'VPN client won’t connect (finance)', status: 'In progress', tone: 'ok' as const },
    { label: 'New hire laptop provisioning', status: 'On track', tone: 'ok' as const },
    { label: 'SLA at risk — mail relay down', status: 'Needs attention', tone: 'crit' as const },
  ];
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
          <div className="flex items-center gap-3">
            <span className={`h-2 w-2 rounded-full ${TONE_DOT_CLASS[r.tone]}`} />
            <span className="text-sm text-white/90">{r.label}</span>
          </div>
          <span className="text-xs font-semibold text-white/60">{r.status}</span>
        </div>
      ))}
    </div>
  );
}

function DevicesMock(): JSX.Element {
  const devices = [
    { icon: LaptopMacOutlinedIcon, name: 'LT-0412', tone: 'ok' as const },
    { icon: DesktopWindowsOutlinedIcon, name: 'WS-1188', tone: 'ok' as const },
    { icon: PhoneIphoneOutlinedIcon, name: 'MB-0091', tone: 'watch' as const },
    { icon: TabletMacOutlinedIcon, name: 'TB-0027', tone: 'ok' as const },
    { icon: LaptopMacOutlinedIcon, name: 'LT-0413', tone: 'crit' as const },
    { icon: DesktopWindowsOutlinedIcon, name: 'WS-1189', tone: 'ok' as const },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {devices.map((d) => {
        const Icon = d.icon;
        return (
          <div key={d.name} className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-4">
            <div className="relative">
              <Icon className="text-white/70" fontSize="medium" />
              <span className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ${TONE_DOT_CLASS[d.tone]}`} />
            </div>
            <span className="text-[11px] font-medium text-white/60">{d.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function NetworkMock(): JSX.Element {
  const sites = [
    { label: 'Firewall', x: 40, y: 30 },
    { label: 'Switch', x: 40, y: 100 },
    { label: 'Access point', x: 40, y: 170 },
  ];
  return (
    <div>
      <svg viewBox="0 0 320 200" className="w-full" role="img" aria-label="Outbound-only agent connecting site devices to the Nexus platform">
        <g stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" fill="none">
          {sites.map((s) => (
            <path key={s.label} d={`M ${s.x + 40} ${s.y + 8} L 190 100`} />
          ))}
          <path d="M 230 100 L 290 100" strokeDasharray="4 4" stroke="#b06bff" />
        </g>
        {sites.map((s) => (
          <g key={s.label}>
            <rect x={s.x} y={s.y - 14} width="80" height="28" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.12)" />
            <text x={s.x + 40} y={s.y + 5} textAnchor="middle" fontSize="10" fill="#f5f6fa" fontFamily="Inter, sans-serif">
              {s.label}
            </text>
          </g>
        ))}
        <circle cx="190" cy="100" r="30" fill="rgba(139,61,240,0.16)" stroke="#8b3df0" strokeWidth="1.5" />
        <text x="190" y="97" textAnchor="middle" fontSize="9.5" fill="#f5f6fa" fontFamily="Inter, sans-serif">Nexus</text>
        <text x="190" y="109" textAnchor="middle" fontSize="9.5" fill="#f5f6fa" fontFamily="Inter, sans-serif">agent</text>
        <circle cx="245" cy="100" r="2.5" fill="#b06bff" className="animate-pulse" />
        <g>
          <circle cx="300" cy="100" r="18" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.12)" />
          <text x="300" y="103" textAnchor="middle" fontSize="8" fill="#f5f6fa" fontFamily="Inter, sans-serif">Nexus</text>
        </g>
      </svg>
      <p className="mt-1 text-center text-[11px] font-medium text-nexus-purple-light">
        Outbound-only — no inbound rule required at the site
      </p>
    </div>
  );
}

function SecurityMock(): JSX.Element {
  const rows = [
    { icon: LockOutlinedIcon, label: 'Credential vault', detail: '42 secrets · rotation on schedule', tone: 'ok' as const },
    { icon: VerifiedUserOutlinedIcon, label: 'Phishing simulation', detail: 'Last campaign · 3 of 58 clicked', tone: 'watch' as const },
    { icon: LockOutlinedIcon, label: 'Vulnerability scan', detail: '2 critical, AI-prioritized', tone: 'crit' as const },
  ];
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => {
        const Icon = r.icon;
        return (
          <div key={r.label} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Icon fontSize="small" className="text-nexus-purple-light" />
              <div>
                <p className="text-sm text-white/90">{r.label}</p>
                <p className="text-xs text-white/50">{r.detail}</p>
              </div>
            </div>
            <span className={`h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT_CLASS[r.tone]}`} />
          </div>
        );
      })}
    </div>
  );
}

function ComplianceMock(): JSX.Element {
  const frameworks = [
    { name: 'NIST CSF', implemented: 62, partial: 24 },
    { name: 'SOC 2', implemented: 55, partial: 30 },
    { name: 'HIPAA', implemented: 48, partial: 28 },
    { name: 'PCI-DSS', implemented: 70, partial: 18 },
  ];
  return (
    <div className="flex flex-col gap-4">
      {frameworks.map((f) => (
        <div key={f.name}>
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="font-semibold text-white/85">{f.name}</span>
            <span className="text-white/45">{f.implemented}% implemented</span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-full bg-white/[0.06]">
            <div className="bg-[#8b3df0]" style={{ width: `${f.implemented}%` }} />
            <div className="bg-[rgba(176,107,255,0.35)]" style={{ width: `${f.partial}%` }} />
          </div>
        </div>
      ))}
      <div className="mt-1 flex gap-4 text-[10.5px] text-white/45">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-nexus-purple" />Implemented</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-nexus-purple/35" />Partial</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white/10" />Planned</span>
      </div>
    </div>
  );
}

function PortalMock(): JSX.Element {
  const people = [
    { initials: 'JM', name: 'Jordan M.', role: 'Client admin', signed: true },
    { initials: 'RL', name: 'Riley L.', role: 'Staff', signed: true },
    { initials: 'AK', name: 'Amara K.', role: 'Client admin', signed: false },
  ];
  return (
    <div className="flex flex-col gap-3">
      {people.map((p) => (
        <div key={p.initials} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-nexus-purple/[0.22] text-[11px] font-bold text-nexus-purple-light">
              {p.initials}
            </span>
            <div>
              <p className="text-sm text-white/90">{p.name}</p>
              <p className="text-xs text-white/50">{p.role}</p>
            </div>
          </div>
          <Chip
            label={p.signed ? 'Agreement signed' : 'Pending signature'}
            size="small"
            className={`h-[22px] border text-[10px] font-bold ${
              p.signed ? 'border-nexus-purple/35 bg-transparent text-nexus-purple-light' : 'border-white/15 bg-transparent text-white/50'
            }`}
          />
        </div>
      ))}
    </div>
  );
}

function BackupMock(): JSX.Element {
  const rows = [
    { icon: MailOutlineIcon, label: 'Exchange Online', detail: '128 mailboxes · verified 04:12', tone: 'ok' as const },
    { icon: CloudOutlinedIcon, label: 'OneDrive', detail: '2.4 TB · incremental complete', tone: 'ok' as const },
    { icon: FolderOutlinedIcon, label: 'SharePoint', detail: '18 sites · SHA-256 verified', tone: 'ok' as const },
    { icon: MailOutlineIcon, label: 'Restore point', detail: 'One mailbox · ready to recover', tone: 'watch' as const },
  ];
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => {
        const Icon = r.icon;
        return (
          <div key={r.label} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Icon fontSize="small" className="text-nexus-purple-light" />
              <div>
                <p className="text-sm text-white/90">{r.label}</p>
                <p className="text-xs text-white/50">{r.detail}</p>
              </div>
            </div>
            <span className={`h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT_CLASS[r.tone]}`} />
          </div>
        );
      })}
      <p className="mt-1 text-center text-[11px] font-medium text-nexus-purple-light">
        Your bucket — Wasabi, AWS S3, or Cloudflare R2 · your keys
      </p>
    </div>
  );
}

function CrmMock(): JSX.Element {
  const deals = [
    { name: 'Riverside District', stage: 'Proposal', tone: 'watch' as const },
    { name: 'Northgate Clinic', stage: 'Qualified', tone: 'ok' as const },
    { name: 'Summit Dental', stage: 'Won → onboarding', tone: 'ok' as const },
    { name: 'Acme Legal', stage: 'New lead', tone: 'watch' as const },
  ];
  return (
    <div className="flex flex-col gap-3">
      {deals.map((d) => (
        <div key={d.name} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className={`h-2 w-2 rounded-full ${TONE_DOT_CLASS[d.tone]}`} />
            <span className="text-sm text-white/90">{d.name}</span>
          </div>
          <Chip
            label={d.stage}
            size="small"
            className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10px] font-bold text-nexus-purple-light"
          />
        </div>
      ))}
    </div>
  );
}

function TenantMock(): JSX.Element {
  const tenants = [
    { name: 'riverside', detail: 'all clear · 128 devices', tone: 'ok' as const },
    { name: 'northgate', detail: '1 alert · 64 devices', tone: 'watch' as const },
    { name: 'summit-dental', detail: 'all clear · 51 devices', tone: 'ok' as const },
    { name: 'acme-legal', detail: '3 alerts · 42 devices', tone: 'crit' as const },
  ];
  return (
    <div className="flex flex-col gap-3">
      {tenants.map((t) => (
        <div key={t.name} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-nexus-purple/[0.22] text-[11px] font-bold text-nexus-purple-light">
              {t.name.slice(0, 2).toUpperCase()}
            </span>
            <div>
              <p className="text-sm text-white/90">{t.name}</p>
              <p className="text-xs text-white/50">{t.detail}</p>
            </div>
          </div>
          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${TONE_DOT_CLASS[t.tone]}`} />
        </div>
      ))}
      <p className="mt-1 text-center text-[11px] font-medium text-nexus-purple-light">
        One HQ console · every tenant hard-isolated
      </p>
    </div>
  );
}

const registry: Record<Feature['illustration'], () => JSX.Element> = {
  tickets: TicketsMock,
  devices: DevicesMock,
  network: NetworkMock,
  backup: BackupMock,
  security: SecurityMock,
  crm: CrmMock,
  tenant: TenantMock,
  compliance: ComplianceMock,
  portal: PortalMock,
};

export function FeatureIllustration({ kind }: { kind: Feature['illustration'] }): JSX.Element {
  const Mock = registry[kind];
  return (
    <IllustrationFrame>
      <Mock />
    </IllustrationFrame>
  );
}
